import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarMonthOutlined,
  DescriptionOutlined,
  EditOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  FactCheckOutlined,
  MoreVertOutlined,
  PendingOutlined,
  SchoolOutlined,
  SearchOutlined,
  TuneOutlined,
  VisibilityOutlined,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import SearchSelect, {
  OptionItem,
} from "components/atoms/SearchSelect/SearchSelect";
import SelectField from "components/atoms/SelectField/SelectField";
import {
  findAvailableParticipantCertificateDetail,
  findAvailableParticipantCertificates,
  type AvailableParticipantCertificateFilters,
} from "API/apiParticipantCertificates";
import {
  AvailableParticipantCertificate,
  AvailableParticipantCertificateDetail,
} from "types/ParticipantCertificate";
import { formatarDataDbParaBr } from "utils/Masks";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import stylesManagement from "styles/ManagementList.module.css";
import styles from "./CertificadosDeclaracoes.module.css";

type SelectOption = OptionItem & { value: string };

type FilterState = AvailableParticipantCertificateFilters & {
  search: string;
  document_type: string;
  status: string;
  ano: string;
  ordenacao: string;
};

type Totals = {
  documentos_disponiveis: number;
  certificados: number;
  declaracoes: number;
  pendentes_emissao: number;
};

const initialFilters: FilterState = {
  search: "",
  document_type: "",
  status: "",
  ano: "",
  ordenacao: "mais_recentes",
};
const quickFilters = [
  { value: "", label: "Todos", tone: "all" },
  { value: "disponiveis", label: "Disponíveis", tone: "available" },
  { value: "certificado", label: "Certificados", tone: "certificate" },
  { value: "declaracao", label: "Declarações", tone: "declaration" },
  { value: "pesquisa_pendente", label: "Pesquisa pendente", tone: "pending" },
  {
    value: "emitido_recentemente",
    label: "Emitidos recentemente",
    tone: "recent",
  },
] as const;

const orderOptions: SelectOption[] = [
  { id: 1, nome: "Mais recentes", value: "mais_recentes" },
  { id: 2, nome: "Mais antigos", value: "mais_antigos" },
  { id: 3, nome: "Nome da ação - A a Z", value: "nome_acao_az" },
  { id: 4, nome: "Nome da ação - Z a A", value: "nome_acao_za" },
  {
    id: 5,
    nome: "Disponíveis para emissão primeiro",
    value: "disponiveis_emissao_primeiro",
  },
  { id: 6, nome: "Pendências primeiro", value: "pendencias_primeiro" },
];

const typeOptions: SelectOption[] = [
  { id: 0, nome: "Todos", value: "" },
  { id: 1, nome: "Certificado", value: "certificate" },
  { id: 2, nome: "Declaração", value: "declaration" },
];

const statusOptions: SelectOption[] = [
  { id: 0, nome: "Todos", value: "" },
  { id: 1, nome: "Disponível para emissão", value: "disponiveis" },
  { id: 2, nome: "Emitido", value: "emitido" },
  { id: 3, nome: "Pesquisa pendente", value: "pesquisa_pendente" },
  { id: 4, nome: "Indisponível", value: "indisponivel" },
];

const formatDate = (date?: string | null) =>
  date ? formatarDataDbParaBr(date.slice(0, 10)) : "-";
const getType = (item: AvailableParticipantCertificate) =>
  item.document_type ||
  item.certificate_template_document_type ||
  "certificate";
const getTypeLabel = (item: AvailableParticipantCertificate) =>
  item.document_type_label ||
  item.certificate_template_document_type_label ||
  (getType(item) === "declaration" ? "Declaração" : "Certificado");
const getStatus = (item: AvailableParticipantCertificate) => {
  if (item.emissao) return { label: "Emitido", tone: "emitted" };
  if (item.pesquisa?.pendente && item.pesquisa.obrigatoria_para_certificado)
    return { label: "Pesquisa pendente", tone: "pending" };
  if (item.pode_emitir) return { label: "Disponível", tone: "available" };
  return { label: "Indisponível", tone: "unavailable" };
};

const CertificatesSkeleton = () => (
  <main className={styles.page} aria-busy="true">
    <Skeleton variant="text" width={330} height={46} />
    <Skeleton variant="text" width={700} height={28} />
    <section className={styles.summaryGrid}>
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} variant="rounded" height={112} />
      ))}
    </section>
    <section className={styles.filterPanel}>
      <div className={styles.skeletonFilters}>
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} variant="rounded" height={42} />
        ))}
      </div>
    </section>
    {Array.from({ length: 3 }, (_, index) => (
      <Skeleton key={index} variant="rounded" height={104} />
    ))}
  </main>
);

const CertificadosDeclaracoes = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");
  const [items, setItems] = useState<AvailableParticipantCertificate[]>([]);
  const [totals, setTotals] = useState<Totals>({
    documentos_disponiveis: 0,
    certificados: 0,
    declaracoes: 0,
    pendentes_emissao: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<FilterState>(initialFilters);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuItem, setMenuItem] =
    useState<AvailableParticipantCertificate | null>(null);
  const [detailItem, setDetailItem] =
    useState<AvailableParticipantCertificateDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response =
        await findAvailableParticipantCertificates(appliedFilters);
      setItems(response.data || []);
      setTotals({
        documentos_disponiveis:
          response.totalizadores?.documentos_disponiveis ??
          response.totalizadores?.documentos_dispiniveis ??
          0,
        certificados: response.totalizadores?.certificados ?? 0,
        declaracoes: response.totalizadores?.declaracoes ?? 0,
        pendentes_emissao: response.totalizadores?.pendentes_emissao ?? 0,
      });
    } catch (requestError: unknown) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Não foi possível carregar os documentos disponíveis.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDocuments();
  }, [appliedFilters]);

  const years = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .map((item) => item.data_inicio_disponibilizacao?.slice(0, 4))
            .filter(Boolean),
        ),
      ).sort((a, b) => Number(b) - Number(a)),
    [items],
  );
  const yearOptions = useMemo<SelectOption[]>(
    () => [
      { id: 0, nome: "Todos", value: "" },
      ...years.map((year, index) => ({
        id: index + 1,
        nome: year,
        value: year,
      })),
    ],
    [years],
  );
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const visibleItems = useMemo(
    () => items.slice((page - 1) * perPage, page * perPage),
    [items, page, perPage],
  );
  const updateFilter = (field: keyof FilterState, value: string) =>
    setFilters((current) => ({ ...current, [field]: value }));
  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };
  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  };
  const selectQuickFilter = (filtro: string) => {
    const next = { ...filters, filtro };
    setFilters(next);
    setAppliedFilters(next);
    setPage(1);
  };
  const changeOrder = (ordenacao: string) => {
    const next = { ...filters, ordenacao };
    setFilters(next);
    setAppliedFilters(next);
    setPage(1);
  };
  const openDetails = async () => {
    if (!menuItem) return;
    try {
      setLoadingDetail(true);
      setDetailItem(
        await findAvailableParticipantCertificateDetail(menuItem.turma_id),
      );
      setMenuAnchor(null);
    } catch (requestError: unknown) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Não foi possível carregar os detalhes do documento.",
      );
    } finally {
      setLoadingDetail(false);
    }
  };

  if (loading) return <CertificatesSkeleton />;

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <h1>Certificados e Declarações</h1>
        <p>
          Acesse e emita certificados e declarações das ações concluídas. Alguns
          documentos podem exigir a conclusão de pesquisa de avaliação.
        </p>
      </header>
      {error && (
        <Alert severity="error" className={styles.feedback}>
          {error}
        </Alert>
      )}
      <section className={styles.summaryGrid} aria-label="Resumo de documentos">
        <SummaryCard
          icon={<DescriptionOutlined />}
          title="Documentos disponíveis"
          value={totals.documentos_disponiveis}
          subtitle="Prontos para emissão"
          tone="blue"
        />
        <SummaryCard
          icon={<WorkspacePremiumOutlined />}
          title="Certificados"
          value={totals.certificados}
          subtitle="Disponíveis ou emitidos"
          tone="green"
        />
        <SummaryCard
          icon={<FactCheckOutlined />}
          title="Declarações"
          value={totals.declaracoes}
          subtitle="Disponíveis ou emitidas"
          tone="purple"
        />
        <SummaryCard
          icon={<PendingOutlined />}
          title="Pendentes de emissão"
          value={totals.pendentes_emissao}
          subtitle="Aguardando pesquisa"
          tone="orange"
        />
      </section>
      <section className={styles.filterPanel}>
        <button
          type="button"
          className={styles.mobileFilterTrigger}
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <span>
            <TuneOutlined /> Filtros e período
          </span>
          {filtersOpen ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
        </button>
        {filtersOpen && (
          <div className={styles.filtersGrid}>
            <label className={styles.searchField}>
              Buscar por nome da ação
              <TextField
                size="small"
                variant="standard"
                placeholder="Digite o nome da ação..."
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && applyFilters()}
                InputProps={{
                  className: stylesInput.inputSoft,
                  disableUnderline: true,
                  startAdornment: (
                    <SearchOutlined className={styles.searchIcon} />
                  ),
                }}
              />
            </label>
            <FilterSelect
              label="Ano"
              value={filters.ano}
              options={yearOptions}
              onChange={(value) => updateFilter("ano", value)}
            />
            <FilterSelect
              label="Tipo de documento"
              value={filters.document_type}
              options={typeOptions}
              onChange={(value) => updateFilter("document_type", value)}
            />
            <FilterSelect
              label="Status / Disponibilidade"
              value={filters.status}
              options={statusOptions}
              onChange={(value) => updateFilter("status", value)}
            />
            <div className={styles.filterActions}>
              <Button
                className={stylesButton.buttonSoftMuted}
                onClick={clearFilters}
              >
                Limpar
              </Button>
              <Button
                variant="contained"
                className={stylesButton.buttonSoftPrimary}
                startIcon={<SearchOutlined />}
                onClick={applyFilters}
              >
                Filtrar
              </Button>
            </div>
          </div>
        )}
      </section>
      <nav className={styles.quickFilters} aria-label="Filtros rápidos">
        {quickFilters.map((quickFilter) => (
          <button
            key={quickFilter.value || "all"}
            type="button"
            className={
              styles.quickFilter +
              " " +
              styles[
                "quick" +
                  quickFilter.tone.charAt(0).toUpperCase() +
                  quickFilter.tone.slice(1)
              ] +
              " " +
              (appliedFilters.filtro === quickFilter.value
                ? styles.quickSelected
                : "")
            }
            onClick={() => selectQuickFilter(quickFilter.value)}
          >
            {quickFilter.label}
          </button>
        ))}
      </nav>
      <section className={styles.listHeader}>
        <strong>
          Encontramos {items.length}{" "}
          {items.length === 1 ? "documento" : "documentos"}
        </strong>
        <div className={styles.orderControl}>
          <span>Ordenar por:</span>
          <SearchSelect
            label=""
            options={orderOptions}
            value={
              orderOptions.find((option) => option.value === filters.ordenacao)
                ?.id ?? 1
            }
            onChange={(value) =>
              changeOrder(
                orderOptions.find((option) => option.id === value)?.value ||
                  "mais_recentes",
              )
            }
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            labelClassname={stylesInput.labelSelect}
            inputClassname={stylesInput.inputSoft}
          />
        </div>
      </section>
      <section className={styles.documentsList}>
        {visibleItems.length === 0 ? (
          <EmptyState onClear={clearFilters} />
        ) : (
          visibleItems.map((item) => {
            const type = getType(item);
            const status = getStatus(item);
            const surveyPending =
              item.pesquisa?.pendente &&
              item.pesquisa?.obrigatoria_para_certificado;
            return (
              <article
                key={
                  String(item.turma_id) +
                  "-" +
                  String(item.certificate_template_id) +
                  "-" +
                  type
                }
                className={styles.documentCard}
              >
                <div
                  className={
                    styles.documentIcon +
                    " " +
                    (type === "declaration"
                      ? styles.declarationIcon
                      : styles.certificateIcon)
                  }
                >
                  {type === "declaration" ? (
                    <DescriptionOutlined />
                  ) : (
                    <WorkspacePremiumOutlined />
                  )}
                </div>
                <div className={styles.documentInfo}>
                  <h2>{item.acao_nome}</h2>
                  <p>
                    <SchoolOutlined /> {item.turma_nome}
                  </p>
                  <div className={styles.documentMeta}>
                    <span>
                      <CalendarMonthOutlined />{" "}
                      {item.emissao
                        ? "Emitido em: " + formatDate(item.emissao.impresso_em)
                        : "Concluído em: " +
                          formatDate(item.data_inicio_disponibilizacao)}
                    </span>
                    {item.data_fim_disponibilizacao ? (
                      <span>
                        <FactCheckOutlined /> Disponível até{" "}
                        {formatDate(item.data_fim_disponibilizacao)}
                      </span>
                    ) : (
                      <span>
                        <FactCheckOutlined /> Válido por prazo indeterminado
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.documentStatus}>
                  <div className={styles.badgeRow}>
                    <span
                      className={
                        styles.badge +
                        " " +
                        (type === "declaration"
                          ? styles.declarationBadge
                          : styles.certificateBadge)
                      }
                    >
                      {getTypeLabel(item)}
                    </span>
                    <span
                      className={
                        styles.badge +
                        " " +
                        styles[
                          "status" +
                            status.tone.charAt(0).toUpperCase() +
                            status.tone.slice(1)
                        ]
                      }
                    >
                      {status.label}
                    </span>
                  </div>
                  <p>
                    {surveyPending
                      ? "Responda à pesquisa de avaliação para liberar a emissão do certificado."
                      : item.emissao
                        ? "Documento emitido em " +
                          formatDate(item.emissao.impresso_em) +
                          "."
                        : item.pode_emitir
                          ? "Documento disponível para emissão."
                          : "Documento ainda não disponível para emissão."}
                  </p>
                </div>
                <div className={styles.documentActions}>
                  <IconButton
                    aria-label="Mais opções"
                    onClick={(event) => {
                      setMenuAnchor(event.currentTarget);
                      setMenuItem(item);
                    }}
                  >
                    <MoreVertOutlined />
                  </IconButton>
                  <Button
                    variant="contained"
                    className={
                      item.emissao
                        ? stylesButton.buttonSoftOutline
                        : stylesButton.buttonSoftPrimary
                    }
                    startIcon={
                      item.emissao ? (
                        <VisibilityOutlined />
                      ) : surveyPending ? (
                        <FactCheckOutlined />
                      ) : (
                        <DescriptionOutlined />
                      )
                    }
                    disabled={
                      !item.emissao && !item.pode_emitir && !surveyPending
                    }
                    onClick={() =>
                      navigate("/emitir-certificado?turma=" + item.turma_id)
                    }
                  >
                    {item.emissao
                      ? "Visualizar documento"
                      : surveyPending
                        ? "Responder pesquisa"
                        : "Emitir documento"}
                  </Button>
                </div>
              </article>
            );
          })
        )}
      </section>
      <footer className={stylesManagement.pagination}>
        <div className={styles.paginationSelect}>
          <span>Resultados por página</span>
          <SelectField
            label=""
            value={String(perPage)}
            onChange={(value) => {
              setPerPage(Number(value));
              setPage(1);
            }}
            options={[{ nome: "10" }, { nome: "20" }]}
            inputClassName={stylesInput.inputSoft}
            disableClearable
            disableTyping
          />
        </div>
        <div className={styles.simplePagination}>
          <Button disabled={page === 1} onClick={() => setPage(1)}>
            Primeira
          </Button>
          <Button
            disabled={page === 1}
            onClick={() => setPage((current) => current - 1)}
          >
            Anterior
          </Button>
          <span>
            Página {page} de {totalPages}
          </span>
          <Button
            disabled={page === totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            Próxima
          </Button>
          <Button
            disabled={page === totalPages}
            onClick={() => setPage(totalPages)}
          >
            Última
          </Button>
        </div>
      </footer>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuItem(null);
        }}
      >
        <MenuItem onClick={() => void openDetails()}>
          <VisibilityOutlined fontSize="small" /> Ver detalhes
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <EditOutlined fontSize="small" /> Solicitar correção
        </MenuItem>
      </Menu>
      <Dialog
        open={Boolean(detailItem) || loadingDetail}
        onClose={() => {
          if (!loadingDetail) setDetailItem(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Detalhes da ação e turma</DialogTitle>
        <DialogContent dividers className={styles.detailsDialog}>
          {loadingDetail ? (
            <div className={styles.detailLoading}>
              <CircularProgress size={28} />
            </div>
          ) : (
            detailItem && (
              <>
                <Detail label="Ação" value={detailItem.acao_nome} />
                <Detail label="Turma" value={detailItem.turma_nome} />
                <Detail
                  label="Tipo de documento"
                  value={getTypeLabel(detailItem)}
                />
                <Detail label="Modalidade" value={detailItem.modalidade_nome} />
                <Detail
                  label="Tipo da ação"
                  value={detailItem.tipo_acao_nome}
                />
                <Detail
                  label="Disponível a partir de"
                  value={formatDate(detailItem.data_inicio_disponibilizacao)}
                />
                <Detail
                  label="Prazo para emissão"
                  value={
                    detailItem.data_fim_disponibilizacao
                      ? formatDate(detailItem.data_fim_disponibilizacao)
                      : "Válido por prazo indeterminado"
                  }
                />
                <Detail label="Endereço" value={formatAddress(detailItem)} />
              </>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailItem(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </main>
  );
};

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}) => (
  <label className={styles.selectField}>
    {label}
    <SearchSelect
      label=""
      options={options}
      value={options.find((option) => option.value === value)?.id ?? 0}
      onChange={(nextValue) =>
        onChange(options.find((option) => option.id === nextValue)?.value || "")
      }
      paperClassname={stylesInput.paper}
      popperClassname={stylesInput.popper}
      labelClassname={stylesInput.labelSelect}
      inputClassname={stylesInput.inputSoft}
    />
  </label>
);
const SummaryCard = ({
  icon,
  title,
  value,
  subtitle,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  value: number;
  subtitle: string;
  tone: "blue" | "green" | "purple" | "orange";
}) => (
  <article className={styles.summaryCard}>
    <div
      className={
        styles.summaryIcon +
        " " +
        styles["summary" + tone.charAt(0).toUpperCase() + tone.slice(1)]
      }
    >
      {icon}
    </div>
    <div>
      <h2>{title}</h2>
      <strong>{value}</strong>
      <p>{subtitle}</p>
    </div>
  </article>
);
const EmptyState = ({ onClear }: { onClear: () => void }) => (
  <div className={styles.emptyState}>
    <DescriptionOutlined />
    <h2>Nenhum certificado ou declaração foi encontrado.</h2>
    <p>
      Ajuste os filtros ou consulte novamente após a conclusão das suas ações de
      qualificação.
    </p>
    <Button
      variant="outlined"
      className={stylesButton.buttonSoftOutline}
      onClick={onClear}
    >
      Limpar filtros
    </Button>
  </div>
);
const Detail = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <p>
      <strong>{label}:</strong> {value}
    </p>
  ) : null;
const formatAddress = (item: AvailableParticipantCertificateDetail) =>
  [
    item.logradouro,
    item.numero,
    item.complemento,
    item.bairro,
    item.cidade,
    item.uf,
    item.cep,
  ]
    .filter(Boolean)
    .join(", ") || null;

export default CertificadosDeclaracoes;
